import {isEmpty} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Plural} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {SimilarInquiry} from '../../../../../models/api/similarInquiriesModel';
import {SynopsisConversationPreview} from './synopsisConversationPreview';
import {SynopsisMessageDetail} from './synopsisHelpers';
import {useSynopsisViewerContext} from './synopsisViewerContext';

interface SynopsisSimilarInquiriesProps {
  similarInquiries?: ReadonlyArray<SimilarInquiry>;
  detail: SynopsisMessageDetail;
  panelId: ViewerPanelId;
}

const StyledSimilarInquiriesWrap = styled.div<{$isExpanded: boolean}>`
  display: flex;
  flex-direction: column;
  padding: 0 16px 0 37px;
  gap: 5px;

  ${(p) => p.$isExpanded && 'margin-bottom: 4px'};
`;

const StyledButton = styled(ChromelessButton)`
  display: flex;
  border-radius: 12px;
  padding: 3px 12px 3px 6px;
  margin-left: -9px;
  gap: 8px;
  align-items: center;
  width: fit-content;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

export const SynopsisSimilarInquiries: FC<SynopsisSimilarInquiriesProps> = ({
  similarInquiries,
  detail,
  panelId,
}) => {
  const {onTrackDataEvent} = useSynopsisViewerContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const onToggleExpanded = () => {
    const newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);

    if (newIsExpanded) {
      onTrackDataEvent(DataEventActionsEnum.EXPAND, ViewerDataEventObject.SYNOPSIS_ITEM_RELATED, {
        related_type: 'similar_inquiries',
        activity_id: detail.activityId,
        type: detail.type,
      });
    }
  };

  if (!similarInquiries || isEmpty(similarInquiries)) {
    return null;
  }

  return (
    <StyledSimilarInquiriesWrap $isExpanded={isExpanded}>
      <StyledButton onClick={onToggleExpanded}>
        <Icon name={isExpanded ? 'chevronDownSmall' : 'chevronRightSmall'} />
        <Plural
          id="synopsis-similar-inquiries"
          one="1 similar conversation"
          other="# similar conversations"
          value={similarInquiries.length}
        />
      </StyledButton>
      {isExpanded &&
        similarInquiries.map(({conversationId, inquiry: similarInquiry}) => (
          <SynopsisConversationPreview
            key={conversationId}
            conversationId={conversationId}
            similarInquiry={similarInquiry}
            panelId={panelId}
            trackEventObjectData={{
              related_type: 'similar_inquiries',
              activity_id: detail.activityId,
              type: detail.type,
            }}
          />
        ))}
    </StyledSimilarInquiriesWrap>
  );
};
